import { useSession } from '@zhiyue/core';
import React from 'react';
import {
  fetchOpenPlatformToken,
  getCachedOpenPlatformToken,
  toOpenPlatformTokenContext,
} from '@/services/openPlatformToken';

type UseOpenPlatformTokenOptions = {
  enabled?: boolean;
};

export const useOpenPlatformToken = ({
  enabled = true,
}: UseOpenPlatformTokenOptions = {}) => {
  const { currentUser, loading: sessionLoading } = useSession();
  const [token, setToken] = React.useState<string>();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error>();

  React.useEffect(() => {
    const currentUserContext = toOpenPlatformTokenContext(currentUser);
    if (!enabled || sessionLoading || !currentUserContext) {
      setToken(undefined);
      setLoading(false);
      setError(undefined);
      return;
    }

    const cachedToken = getCachedOpenPlatformToken(currentUserContext);
    if (cachedToken) {
      setToken(cachedToken.accessToken);
      setLoading(false);
      setError(undefined);
      return;
    }

    let active = true;
    setToken(undefined);
    setLoading(true);
    setError(undefined);
    void fetchOpenPlatformToken(currentUserContext)
      .then(({ accessToken }) => {
        if (active) {
          setToken(accessToken);
        }
      })
      .catch((reason: unknown) => {
        if (active) {
          setError(
            reason instanceof Error
              ? reason
              : new Error('开放平台令牌获取失败'),
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    currentUser,
    currentUser?.memberUuid,
    currentUser?.tenantUuid,
    enabled,
    sessionLoading,
  ]);

  return { token, loading: sessionLoading || loading, error };
};
